// ─────────────────────────────────────────────
//  mobileConfig — resolve facility.mobileConfig into what the phone app shows.
//
//  The web Setup page writes a per-module visibility map onto the facility doc;
//  every installed phone reads it live. The contract is deliberately FAIL-OPEN:
//  an unconfigured or garbage mobileConfig renders every module, because the
//  failure modes are asymmetric — a false "show" is a cluttered screen, a false
//  "hide" is a CRNA who cannot find the Emergency button.
//
//  But fail-open must never be SILENT fail-open (this codebase was burned once
//  by "no geofence pin means allow"), so the resolver reports which of five
//  states it is in: pending / unconfigured / invalid / unsupportedVersion /
//  configured. "Nobody ever decided anything" and "somebody wrote garbage" are
//  distinguishable, and each gets its own About-row copy.
//
//  Strict parse: only literal true/false count. The string "false" is truthy
//  in JS and Number(null) === 0 — the same coercion class that once put a
//  geofence in the Gulf of Guinea. Anything non-boolean resolves to VISIBLE
//  and is recorded in `problems`.
//
//  Pure — no Firebase, no imports at all. This is not stylistic: a pure parser
//  cannot participate in a capacitor:// boot-path failure (see the getAnalytics
//  and getAuth incidents recorded in src/firebase/config.js).
// ─────────────────────────────────────────────

// Bumped ONLY when the MEANING of an existing key changes, never when a module
// is added (adding a module is backward-compatible: absent key => visible).
export const MOBILE_CONFIG_VERSION = 1;

// The registry is a property of the BUILD, not of a tenant — no facility ids
// here, ever. Ids are permanent (renaming one silently un-hides it at every
// facility that had hidden it) and contain no dots (Firestore treats "." as a
// field-path separator in updateDoc, which would silently nest the key).
//
// `locked: true` modules are force-rendered regardless of the doc:
//  - alertsEmergency: an admin in an office must not be able to remove the
//    Emergency control from a phone standing in an OR. Patient-safety control.
//  - roomCard: the identity anchor of the dashboard — hiding it leaves
//    controls with no context about which room they act on.
export const MODULE_REGISTRY = [
  { id: "roomCard",          label: "Assignment card",       group: "room",       locked: true },
  { id: "roomStatus",        label: "Room status control",   group: "room" },
  { id: "breaksRequest",     label: "Take a break",          group: "breaks" },
  { id: "breaksGive",        label: "Give a break",          group: "breaks" },
  { id: "breaksBoard",       label: "Break board",           group: "breaks" },
  { id: "alertsEmergency",   label: "Emergency alert",       group: "alerts",     locked: true },
  { id: "alertsUrgency",     label: "Urgency alert",         group: "alerts" },
  // The bell surfaces delivered alerts/notifications, so it lives with the
  // alerts group in Setup (the spec's six groups have no "notifications" row).
  { id: "notificationsBell", label: "Notifications",         group: "alerts" },
  { id: "messagesCenter",    label: "Message Center",        group: "messages" },
  { id: "messagesChannels",  label: "Message channels",      group: "messages" },
  { id: "messagesCompose",   label: "New message / group",   group: "messages" },
  { id: "supervisorRooms",   label: "Supervisor room grid",  group: "supervisor" },
  { id: "supervisorMdCall",  label: "MDA call line",         group: "supervisor" },
  { id: "sessionSignOff",    label: "Sign off",              group: "session" },
  // The read-only day chart on the phone (MobileTimeline). Added 2026-08-18;
  // adding a module is backward-compatible — an absent key means visible, so
  // every existing facility doc gets it without a migration.
  { id: "dayTimeline",       label: "Timeline",              group: "room" },
];

// Spec §1.5 names the registry MOBILE_MODULES; keep both names pointing at the
// same array so neither caller convention breaks.
export const MOBILE_MODULES = MODULE_REGISTRY;

// Derived from the registry so there is exactly one place a module can be
// declared locked — a divergent hand-maintained list would be a latent bug.
export const LOCKED_MODULE_IDS = MODULE_REGISTRY.filter((m) => m.locked === true).map((m) => m.id);

// "Plain object" excludes arrays deliberately: Firestore can store arrays, and
// `typeof [] === "object"`, so a bare typeof check would let `modules: []`
// parse as an empty (all-on) config instead of being flagged invalid.
function isPlainObject(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

// Problem strings name the value AND its type ('"false" (string)') because the
// person reading them is an admin trying to fix a doc, not a JS developer.
function describeValue(v) {
  if (v === null) return "null";
  if (typeof v === "string") return `"${v}" (string)`;
  if (Array.isArray(v)) return "an array";
  if (typeof v === "object") return "an object";
  return `${String(v)} (${typeof v})`;
}

// Every exit path goes through here so the contract invariants hold in every
// state: the modules map is always COMPLETE (every registry id present) and
// always strictly boolean. Consumers never need to guard a lookup.
function baseResult(status, overrides = {}) {
  const modules = {};
  for (const m of MODULE_REGISTRY) modules[m.id] = true;
  return {
    status,
    version: MOBILE_CONFIG_VERSION,
    modules,
    hidden: [],
    locked: [],
    unknownKeys: [],
    problems: [],
    allOptionalHidden: false,
    ...overrides,
  };
}

/**
 * Resolve a facility doc's mobileConfig into the five-state result (§1.4 of
 * PLATFORM-NOTES). Never throws — all access is guarded, there is no
 * JSON.parse, no regex, no `new`.
 *
 * @param {object|null|undefined} facilityDoc — the facility document (or null)
 * @param {object} [opts]
 *   @param {boolean} [opts.loaded] — pass false while the snapshot has not
 *     arrived yet; the shell must render all modules rather than gate on a
 *     listener that may never fire (R2: first launch offline delivers NO
 *     snapshot at all, not an empty one).
 * @returns {{status: string, version: number, modules: Object<string,boolean>,
 *   hidden: string[], locked: string[], unknownKeys: string[],
 *   problems: string[], allOptionalHidden: boolean}}
 */
export function resolveMobileConfig(facilityDoc, opts = {}) {
  // Rule 1 — not loaded yet. Distinct from "unconfigured" so the About row can
  // say "waiting" instead of claiming a decision was made.
  if (opts && opts.loaded === false) return baseResult("pending");

  // Rule 2 — no doc, or no mobileConfig key: the day-one common case.
  // Cheap, silent, all on. Non-object docs (a number, a string) cannot carry
  // the key, so they land here too rather than throwing on property access.
  const raw = isPlainObject(facilityDoc) ? facilityDoc.mobileConfig : undefined;
  if (raw === undefined) return baseResult("unconfigured");

  // Rule 3 — the key exists but is not a map. Somebody wrote something, so
  // this is "invalid" (repairable from Setup), never "unconfigured".
  if (!isPlainObject(raw)) {
    return baseResult("invalid", { problems: ["mobileConfig is not an object"] });
  }

  // Rule 4 — version gate, checked before modules: a higher `v` means the
  // SEMANTICS may have changed (e.g. a future v2 where absent means hidden),
  // and an app that cannot be sure it understands the semantics must not act
  // on them. Show everything and say the app is older than the settings.
  const v = raw.v;
  if (v !== undefined) {
    if (typeof v !== "number" || !Number.isFinite(v)) {
      return baseResult("invalid", {
        problems: [`mobileConfig.v is ${describeValue(v)}, expected a number`],
      });
    }
    if (v > MOBILE_CONFIG_VERSION) return baseResult("unsupportedVersion");
  }
  const version = v === undefined ? MOBILE_CONFIG_VERSION : v;

  // Rule 5 — modules must be a map. A present-but-empty mobileConfig lands
  // here: somebody wrote the key, so something went wrong — flag it.
  const rawModules = raw.modules;
  if (!isPlainObject(rawModules)) {
    return baseResult("invalid", {
      problems: [
        rawModules === undefined
          ? "mobileConfig.modules is missing"
          : `mobileConfig.modules is ${describeValue(rawModules)}, expected an object`,
      ],
    });
  }

  // Rules 6-9 — per-key strict parse over the DOC's keys. Registry ids absent
  // from the doc simply keep their baseResult() default of true, which is what
  // makes a new app + old config safe (a newly shipped module arrives visible).
  const result = baseResult("configured", { version });
  const lockedIds = new Set(LOCKED_MODULE_IDS);
  const knownIds = new Set(MODULE_REGISTRY.map((m) => m.id));
  for (const key of Object.keys(rawModules)) {
    // Rule 7 — unknown keys are dropped, not fatal: an old app reading a newer
    // facility's config must ignore modules it has never heard of.
    if (!knownIds.has(key)) {
      result.unknownKeys.push(key);
      continue;
    }
    const value = rawModules[key];
    // An explicitly-undefined value is indistinguishable in meaning from an
    // absent key (Firestore cannot even store it) — normal, not a problem.
    if (value === undefined) continue;
    // Rule 6 — only a literal boolean is honoured; everything else is VISIBLE
    // plus a recorded problem naming the key, the value, and its type.
    if (typeof value !== "boolean") {
      result.problems.push(`modules.${key} is ${describeValue(value)}, expected a boolean`);
      continue;
    }
    if (value === false) {
      if (lockedIds.has(key)) {
        // Rule 9 — locked ids stay force-enabled. Not an error: report the
        // override so Setup and About can show that it happened.
        result.locked.push(key);
      } else {
        result.modules[key] = false;
        result.hidden.push(key);
      }
    }
  }

  // Sorted so consumers (and tests) get deterministic lists regardless of the
  // doc's key insertion order.
  result.hidden.sort();
  result.locked.sort();
  result.unknownKeys.sort();

  // True only for a VALID config that hides every non-locked module — the
  // explicitly-all-off decision. A fail-open all-on (unconfigured/invalid)
  // must never masquerade as that decision, which is why this is computed
  // only on the configured path.
  const optionalCount = MODULE_REGISTRY.length - LOCKED_MODULE_IDS.length;
  result.allOptionalHidden = result.hidden.length === optionalCount;

  return result;
}

/**
 * Convenience for render guards. An unknown module id — or a malformed
 * resolved object — is never hidden by accident: only an explicit `false` in
 * the complete modules map hides anything.
 */
export function isModuleVisible(resolved, id) {
  return !(resolved && resolved.modules && resolved.modules[id] === false);
}
